import { inject, Injectable, Injector } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ZIMCallCancelConfig, ZIMCallUserInfo, ZIMCallUserState } from 'zego-zim-web';

import { LocalNotificationService } from '@app/services/notification.service';

import { ZegoMediaControlService } from '../../../services/zego-media-control.service';
import { ZimService } from '../../../services/zim.service';
import { safeJsonParse } from '../../../utils/utils';
import { CALL_END_REASON, CALL_STATE } from '../constants';
import { CallAudioService } from './call-audio.service';
import { CallSessionStoreService } from './call-session-store.service';
import { CallStoreService } from './call-store.service';
import { CallZimService } from './call-zim.service';

interface CallUserStateChangedData {
  callID: string;
  callUserList: ZIMCallUserInfo[];
}

@Injectable({ providedIn: 'root' })
export class CallZimUserStateHandlerService {
  private injector = inject(Injector);
  private callSessionStore = inject(CallSessionStoreService);
  private zimService = inject(ZimService);
  private callAudio = inject(CallAudioService);
  private callZimService = inject(CallZimService);
  private toastr = inject(ToastrService);
  private zegoMediaControlService = inject(ZegoMediaControlService);
  private readonly localNotificationService = inject(LocalNotificationService);

  private _callStore: CallStoreService | null = null;
  private get callStore(): CallStoreService {
    if (!this._callStore) {
      this._callStore = this.injector.get(CallStoreService);
    }
    return this._callStore;
  }

  async handle(
    user: ZIMCallUserInfo,
    selfUser: boolean,
    data: CallUserStateChangedData,
  ): Promise<void> {
    switch (user.state) {
      case ZIMCallUserState.Inviting:
        return;
      case ZIMCallUserState.Received:
        if (!selfUser) {
          if (this.callStore.callState() === CALL_STATE.OUTGOING) {
            this.callSessionStore.setCurrentCallState(CALL_STATE.RINGING);
          }
        }
        return;
      case ZIMCallUserState.Accepted: {
        this.handleAccept(user, selfUser, data);
        return;
      }
      case ZIMCallUserState.Rejected: {
        const extendedData = safeJsonParse<{ reason?: string; callID?: string }>(user.extendedData);
        if (extendedData && extendedData.reason === 'busy_pre_room_calling') {
          const currentCall = this.callStore.currentCall();
          if (currentCall && currentCall.callId && extendedData.callID !== currentCall.callId)
            return;
          this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.BUSY);
        } else {
          this.localNotificationService.clearCallAlert(data.callID);
          this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.REJECTED);
        }
        return;
      }
      case ZIMCallUserState.Cancelled:
        return;
      case ZIMCallUserState.Timeout:
        await this.handleTimeout(data);
        return;
      case ZIMCallUserState.Quit:
        if (this.callStore.isSwitchCall(user.extendedData)) return;
        this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.ENDED);
        return;
      case ZIMCallUserState.Ended:
        if (this.callStore.isSwitchCall(user.extendedData)) return;
        this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.ENDED);
        return;
      case ZIMCallUserState.NotYetReceived:
        return;
      case ZIMCallUserState.BeCancelled:
        this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.CANCELLED);
        return;
    }
  }

  private async handleTimeout(data: CallUserStateChangedData): Promise<void> {
    console.log('[DEBUG-CALL]', 'handleTimeout due to user state change', JSON.stringify(data));
    const call = this.callStore.currentCall();
    if (!call || call.callId !== data.callID) return;
    if (call.inviter !== this.zimService.userId) {
      // giữ cuộc gọi chờ WAITING nếu còn pending khác call hiện tại.
      const pending = this.callStore.pendingInvitation();
      this.callStore.reset(!!pending && pending.callId !== call.callId);
      return;
    }

    const invitees: string[] = this.callStore.participants().map((p) => p.userId);
    this.callStore.showMissingCallNotification();
    this.callStore.handleCallInvitationError(data.callID, this.callStore.resolveTimeoutEndReason());

    const config: ZIMCallCancelConfig = { extendedData: '' };
    await this.callZimService.callCancel(invitees, call.callId, config);
  }

  private async handleAccept(
    user: ZIMCallUserInfo,
    selfUser: boolean,
    data: CallUserStateChangedData,
  ): Promise<void> {
    this.callAudio.stop();
    const call = this.callStore.currentCall();
    const pending = this.callStore.pendingInvitation();
    // console.log(
    //   `Check call accepted: ${user.userID},
    //            callState: ${this.callStore.callState()},
    //            callId: ${call?.callId},
    //            currentCall: ${call ? JSON.stringify(call) : null},
    //            callInvite: ${pending ? JSON.stringify(pending) : null}
    //            extendedData: ${user.extendedData},
    //            isMeClickAccept: ${this.callStore.isMeClickAccept()}`,
    // );

    // cuộc gọi thứ 3 -> khi có cuộc gọi đến được chấp nhận ở app rồi
    if (call && call.callId !== data.callID) {
      if (
        user &&
        user.state === ZIMCallUserState.Accepted &&
        !this.callStore.isMeClickAccept() &&
        this.callStore.callState() === CALL_STATE.CONNECTED
      ) {
        if (call.inviter === this.callStore.zimService.userId) {
          await this.callStore.callZimService.callEnd(call.callId, { extendedData: '' });
        } else {
          await this.callStore.callZimService.callQuit(call.callId, { extendedData: '' });
        }

        this.callStore.callZegoService.logoutRoom(call.callId);
        this.callStore.callZegoService.stopPubishAndDestroyStream(
          this.callStore.localStream(),
          this.callStore.localStreamId()!,
        );

        if (this.zegoMediaControlService.mediaState().isScreenSharing) {
          this.zegoMediaControlService.stopScreenShare();
        }
        this.callStore.isLoginRoom.set(false);

        this.callStore.reset();
        this.toastr.error('Cuộc gọi đã được trả lời trên thiết bị khác.');
        this.localNotificationService.clearCallAlert(data.callID);
        return;
      }
    }

    // khi có cuộc gọi đến được chấp nhận ở app rồi
    if (
      pending &&
      pending.callId === data.callID &&
      selfUser &&
      !this.callStore.isMeClickAccept()
    ) {
      this.callStore.reset();
      this.toastr.error('Cuộc gọi đã được trả lời trên thiết bị khác.');
      this.localNotificationService.clearCallAlert(data.callID);
      return;
    }

    // Cập nhật trạng thái cho người gọi
    if (call && user.userID !== call.inviter && call.inviter === this.zimService.userId) {
      if (this.callStore.callState() === CALL_STATE.CONNECTED) return;
      this.callSessionStore.setCurrentCallState(CALL_STATE.CONNECTING);
      this.callStore.publishCallStream(call!.callId, call!.type);
    }
  }
}
